import * as Tabs from '@radix-ui/react-tabs'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { ActionBar } from '@/components/invoices/ActionBar'
import { ActivityTab } from '@/components/invoices/ActivityTab'
import { DistributionTab } from '@/components/invoices/DistributionTab'
import { InvoiceStatusPill } from '@/components/invoices/InvoiceStatusPill'
import { OpenDurationBadge } from '@/components/invoices/OpenDurationBadge'
import { InvoiceEmailBanner } from '@/components/invoices/InvoiceEmailBanner'
import { InvoiceStaleBanner } from '@/components/invoices/InvoiceStaleBanner'
import { LinesTab } from '@/components/invoices/LinesTab'
import { Card } from '@/components/ui/Card'
import { Skeleton } from '@/components/ui/Skeleton'
import { SlidingTabsList, slidingTabTrigger } from '@/components/ui/SlidingTabsList'
import { useIsAdmin } from '@/lib/auth'
import { useInvoice } from '@/lib/invoice-queries'

const SOURCE_LABEL: Record<string, string> = {
  TIMESHEET: 'Uit uren',
  CUSTOM: 'Custom',
  RECURRING: 'Terugkerend',
  IMPORTED: 'Geïmporteerd',
}

export function InvoiceDetailPage({ invoiceId }: { invoiceId: string }) {
  const invoiceQuery = useInvoice(invoiceId)
  const isAdmin = useIsAdmin()
  const search = useSearch({ strict: false }) as { tab?: string }
  const navigate = useNavigate()
  const tab = search.tab ?? 'lines'
  function setTab(next: string) {
    navigate({
      to: '/facturen/$id',
      params: { id: invoiceId },
      search: (prev) => ({ ...prev, tab: next === 'lines' ? undefined : next }),
      replace: true,
    })
  }

  if (invoiceQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-52" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)]">
          <Skeleton className="aspect-[1/1.414] w-full rounded-card" />
          <div className="space-y-3">
            <Skeleton className="h-9 w-64 rounded-lg" />
            <Skeleton className="h-40 w-full rounded-card" />
          </div>
        </div>
      </div>
    )
  }

  if (invoiceQuery.isError || !invoiceQuery.data) {
    return (
      <div className="space-y-4">
        <Link to="/facturen" className="inline-flex items-center gap-1.5 text-sm text-ink-faint hover:text-ink">
          <ArrowLeft className="h-4 w-4" />
          Facturen
        </Link>
        <p className="rounded-card border border-dashed border-hairline px-4 py-10 text-center text-sm text-ink-faint">
          Deze factuur bestaat niet of je hebt er geen toegang toe.
        </p>
      </div>
    )
  }

  const invoice = invoiceQuery.data
  // Oninbaar is bewust niet readonly: daar hoort de Heropenen-knop nog bij.
  const readonly = invoice.status === 'VOIDED' || invoice.status === 'REPLACED' || invoice.status === 'WITHDRAWN'

  return (
    <div className="space-y-6">
      <div>
        <Link to="/facturen" className="mb-3 inline-flex items-center gap-1.5 text-sm text-ink-faint hover:text-ink">
          <ArrowLeft className="h-4 w-4" />
          Facturen
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.022em] text-ink">
            Factuur {invoice.fullNumber ?? 'concept'}
          </h1>
          <InvoiceStatusPill status={invoice.status} detail={invoice} />
          <OpenDurationBadge daysOpen={invoice.daysOpen} needsAttention={invoice.needsAttention} />
          <span className="rounded-pill bg-fill-2 px-2 py-0.5 text-xs font-medium text-ink-faint">
            {SOURCE_LABEL[invoice.source] ?? invoice.source}
          </span>
          <span className="text-sm text-ink-faint">
            {invoice.clientName} · {invoice.subject}
            {invoice.periodLabel ? ` · Periode ${invoice.periodLabel}` : ''}
          </span>
        </div>
      </div>

      <InvoiceEmailBanner invoice={invoice} />
      <InvoiceStaleBanner invoice={invoice} onGoToActivity={() => setTab('activity')} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)]">
        <div className="lg:sticky lg:top-20 lg:self-start">
          <Card inset className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-hairline bg-fill-1 px-4 py-2.5 text-xs font-medium text-ink-faint">
              <span>Preview</span>
              <a
                href={`/api/invoices/${invoice.id}/pdf`}
                target="_blank"
                rel="noreferrer"
                className="rounded-md px-2 py-1 font-medium text-accent transition-colors hover:bg-accent/10"
              >
                Open PDF
              </a>
            </div>
            <div className="bg-surface-2 p-4">
              <div className="aspect-[1/1.414] overflow-hidden rounded-xs bg-white ring-1 ring-black/10 shadow-[0_2px_8px_rgba(0,0,0,0.10),0_12px_28px_-12px_rgba(0,0,0,0.28)]">
                {invoiceQuery.isSuccess ? (
                  <iframe
                    title="Factuurpreview"
                    src={`/api/invoices/${invoice.id}/pdf?v=${invoiceQuery.dataUpdatedAt}#toolbar=0&navpanes=0&view=FitH`}
                    className="h-full w-full border-0"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <Skeleton className="h-5/6 w-5/6" />
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        <div>
          <Tabs.Root value={tab} onValueChange={setTab}>
            <SlidingTabsList value={tab}>
              <Tabs.Trigger value="lines" className={slidingTabTrigger}>
                Regels
              </Tabs.Trigger>
              <Tabs.Trigger value="distribution" className={slidingTabTrigger}>
                Verdeling
              </Tabs.Trigger>
              <Tabs.Trigger value="activity" className={slidingTabTrigger}>
                Activiteit
              </Tabs.Trigger>
            </SlidingTabsList>

            <Tabs.Content value="lines" className="mt-4">
              <Card>
                <LinesTab invoice={invoice} />
              </Card>
            </Tabs.Content>
            <Tabs.Content value="distribution" className="mt-4">
              <Card>
                <DistributionTab invoice={invoice} />
              </Card>
            </Tabs.Content>
            <Tabs.Content value="activity" className="mt-4">
              <Card>
                <ActivityTab invoice={invoice} />
              </Card>
            </Tabs.Content>
          </Tabs.Root>
        </div>
      </div>

      {readonly || !isAdmin ? null : <ActionBar invoice={invoice} />}
    </div>
  )
}
